import React, { useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Music2, Loader, FolderPlus, ArrowRight, Eye, Plus, Trash2 } from 'lucide-react';
import { useCollections } from '../contexts/CollectionsContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { createCollection, deleteCollection } from '../services/api';
import { CreateCollectionModal } from './CreateCollectionModal';
import { useConfirm } from '../hooks/useConfirm';

// Temporary kill-switch for the collection-level view_count chip — the file-metadata
// fanout that powers collection view counters is being rebuilt; chip stays hidden
// until the new path lands.
const SHOW_COLLECTION_VIEW_COUNT = false;

interface RecentCollectionsProps {
  limit?: number;
  showViewAll?: boolean;
}

export const RecentCollections: React.FC<RecentCollectionsProps> = ({
  limit = 5,
  showViewAll = true,
}) => {
  const navigate = useNavigate();
  const { collections: allCollections, loading, error, refresh } = useCollections();
  const { getValidToken } = useAuth();
  const { showToast } = useToast();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDelete = useCallback(
    async (collectionId: string, collectionName: string, e: React.MouseEvent) => {
      e.stopPropagation();

      const confirmed = await confirm({
        title: 'Delete Collection',
        message: `Are you sure you want to delete "${collectionName}"? Files in this collection will not be deleted.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger',
      });

      if (!confirmed) return;

      try {
        setDeletingId(collectionId);
        const token = await getValidToken();
        if (!token) {
          showToast('Authentication required', 'error');
          return;
        }
        await deleteCollection(collectionId, token);
        showToast('Collection deleted', 'success');
        refresh();
      } catch (err: any) {
        console.error('Failed to delete collection:', err);
        showToast(err?.message || 'Failed to delete collection', 'error');
      } finally {
        setDeletingId(null);
      }
    },
    [confirm, getValidToken, showToast, refresh]
  );

  const handleCreate = useCallback(
    async (data: { name: string; description?: string; visibility: 'public' | 'private' }) => {
      const token = await getValidToken();
      if (!token) {
        showToast('Authentication required', 'error');
        throw new Error('no token');
      }
      try {
        const newCollection = await createCollection(data.name, data.description, data.visibility, token);
        showToast('Collection created', 'success');
        refresh();
        navigate(`/collection/${newCollection.collection_id}`);
      } catch (err: any) {
        console.error('Failed to create collection:', err);
        showToast(err?.message || 'Failed to create collection', 'error');
        throw err;
      }
    },
    [getValidToken, showToast, refresh, navigate]
  );

  const collections = useMemo(() => {
    return [...allCollections]
      .sort((a, b) => b.updated_at - a.updated_at)
      .slice(0, limit);
  }, [allCollections, limit]);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <motion.div
      className="rounded-2xl border p-6 shadow-sm"
      style={{
        backgroundColor: 'var(--background-surface)',
        borderColor: 'var(--border-color)',
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      {/* Header — same shape as RecentFiles ("text-xl font-bold" title, right-aligned
          actions, no icon, no border-b). Drops the FolderPlus icon that was redundant
          with the "Collections" word and drops the divider so the two dashboard cards
          share one visual grammar. */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Recent Collections
        </h2>

        {collections.length > 0 && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsCreateOpen(true)}
              className="flex items-center gap-1 text-sm font-medium text-soundwave-500 hover:text-soundwave-400 transition-colors"
              aria-label="Create new collection"
            >
              <Plus className="w-4 h-4" />
              <span>New</span>
            </button>
            {showViewAll && (
              <button
                onClick={() => navigate('/collections')}
                className="flex items-center gap-1 text-sm font-medium text-soundwave-500 hover:text-soundwave-400 transition-colors"
              >
                <span>View All</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-6 h-6 text-soundwave-500 animate-spin" />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="py-6 text-center">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {error}
          </p>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && collections.length === 0 && (
        <div className="py-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-soundwave-500/10 border border-soundwave-500/20 flex items-center justify-center">
            <FolderPlus className="w-8 h-8 text-soundwave-400" />
          </div>
          <h3 className="text-base font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            No Collections Yet
          </h3>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
            Create your first collection to organize and share your audio files
          </p>
          <motion.button
            onClick={() => setIsCreateOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-soundwave-500/10 hover:bg-soundwave-500/20 text-soundwave-500 border border-soundwave-500/30 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FolderPlus className="w-4 h-4" />
            <span className="font-medium">Create Collection</span>
          </motion.button>
        </div>
      )}

      {/* Collections List */}
      {!loading && !error && collections.length > 0 && (
        <div className="space-y-2">
          {collections.map((collection, index) => (
            <motion.div
              key={collection.collection_id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
              onClick={() => navigate(`/collection/${collection.collection_id}`)}
              className="flex items-center gap-3 p-3 rounded-xl transition-all cursor-pointer hover:bg-neutral-800/30 group"
            >
              {/* Artwork Thumbnail */}
              <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gradient-to-br from-soundwave-500/20 to-soundwave-600/30 flex items-center justify-center">
                {collection.artwork_thumbnail_url || collection.artwork_url ? (
                  <img
                    src={collection.artwork_thumbnail_url || collection.artwork_url}
                    alt={collection.collection_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Music2 className="w-6 h-6 text-soundwave-400" />
                )}
              </div>

              {/* Collection Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate group-hover:text-soundwave-400 transition-colors" style={{ color: 'var(--text-primary)' }}>
                  {collection.collection_name}
                </p>
                <div className="flex items-center gap-2 text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                  <span>{collection.file_count} {collection.file_count === 1 ? 'track' : 'tracks'}</span>
                  <span>•</span>
                  <span className="capitalize">{collection.visibility}</span>
                  {/* Collection view_count chip hidden while the file-metadata fanout
                      is being rebuilt — the collection-level counter shares the same data
                      path and will report 0 until the rebuild lands. Field on the Collection
                      type preserved; this is a render-side mute. */}
                  {SHOW_COLLECTION_VIEW_COUNT && collection.view_count !== undefined && collection.view_count > 0 && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {collection.view_count}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Updated Date */}
              <div className="flex-shrink-0 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {formatDate(collection.updated_at)}
              </div>

              {/* Delete Button */}
              <motion.button
                onClick={(e) => handleDelete(collection.collection_id, collection.collection_name, e)}
                disabled={deletingId === collection.collection_id}
                className="flex-shrink-0 p-2 rounded-lg hover:bg-red-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: deletingId === collection.collection_id ? 1 : 1.1 }}
                whileTap={{ scale: deletingId === collection.collection_id ? 1 : 0.9 }}
                title="Delete collection"
                aria-label="Delete collection"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </motion.button>
            </motion.div>
          ))}
        </div>
      )}

      <CreateCollectionModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreate={handleCreate}
      />

      <ConfirmDialogComponent />
    </motion.div>
  );
};
